import { handleFormServerErrors } from 'helpers/errorHandling';
import type { FormEvent, PropsWithChildren } from 'react';
import React from 'react';
import type { DefaultValues, FieldValues, SubmitHandler, UseFormReturn } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';

type FormWrapperProps<TFieldValues extends FieldValues> = PropsWithChildren<{
  defaultValues?: DefaultValues<TFieldValues>;
  onSubmit: SubmitHandler<TFieldValues>;
  className?: string;
  onInit?: (methods: UseFormReturn<TFieldValues>) => void; // Use only if necessary and need to work with form methods inside parent otherwise wrap fields into separate component and use useFormContext()
}>;

const FormWrapper = <TFieldValues extends FieldValues>({
  children,
  onSubmit,
  className,
  defaultValues,
  onInit
}: FormWrapperProps<TFieldValues>) => {
  const methods = useForm<TFieldValues>({ defaultValues });

  const submit: SubmitHandler<TFieldValues> = async (data, event) => {
    try {
      await onSubmit(data, event);
    } catch (e) {
      handleFormServerErrors(e, methods.setError);
    }
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.stopPropagation();
    void methods.handleSubmit(submit)(event);
  };

  React.useEffect(() => {
    onInit?.(methods);
  }, [onInit, methods]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleFormSubmit} className={className}>
        {children}
      </form>
    </FormProvider>
  );
};

export default FormWrapper;
